import { useState } from "react";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Button } from "@/shared/components/button";
import { AppModal } from "@/shared/components/dialog";
import { EmptyState } from "@/shared/components/EmptyState";
import { formatRelativeTime } from "@/shared/lib/formatRelativeTime";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { ISSUE_LABELS } from "@/features/patient/medications/components/medicationReviewBadges";
import { PhysicianPicker } from "@/features/admin/physicians/components/PhysicianPicker";
import { assignReviewItemApi, listAdminReviewQueueApi, type AdminQueueItem } from "@/portals/admin/api/adminReviewQueueApi";
import type { PhysicianListItem } from "@/portals/physician/api/types";
import { useAdminSocketEvent } from "@/lib/adminSocket";
import { useAdminReviewQueueStore } from "@/stores/adminReviewQueueStore";
import { useCachedQuery } from "@/shared/hooks/useCachedQuery";
import { adminQueryKeys, fetchAdminPhysicians } from "@/portals/admin/api/adminQueryKeys";

export const MESSAGES = {
  empty: "No items waiting for a physician.",
  assigned: "Item assigned.",
  notAssignable: "This item was already claimed or assigned.",
  notEligible: "This physician can't review this item.",
  physicianGone: "That physician is no longer available.",
  itemGone: "This item isn't available.",
};

const BRAND = "!h-auto !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-4 !py-2 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white disabled:!cursor-not-allowed disabled:!opacity-60";
const OUTLINE = "!h-auto !rounded-lg !border-[#E5E7EB] !px-4 !py-2 !text-sm !font-medium !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]";

export function ReviewQueueView() {
  const [target, setTarget] = useState<AdminQueueItem | null>(null);
  const [choice, setChoice] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const refreshSummary = useAdminReviewQueueStore((s) => s.refreshSummary);

  // Cached: coming back to this page shows the last list at once, then refreshes it quietly.
  const queue = useCachedQuery(
    adminQueryKeys.reviewQueue,
    async () => {
      const { data, error } = await listAdminReviewQueueApi();
      return { data: data?.items ?? null, error };
    },
    { onError: (error) => toastError(error?.message ?? "Unable to load the review queue.") },
  );
  const items = queue.data ?? [];
  const load = queue.refresh;

  // Live: the server says the queue changed → refetch the list (the layout refetches the summary).
  useAdminSocketEvent("review_queue:changed", () => void load());

  // Only fetched while the assign dialog is open; reopening it shows the cached list instantly.
  const physicianList = useCachedQuery(target ? adminQueryKeys.physicians : null, fetchAdminPhysicians, {
    onError: (error) => toastError(error?.message ?? "Unable to load physicians."),
  });
  const physicians: PhysicianListItem[] = physicianList.data ?? [];
  const loadPhysicians = physicianList.refresh;

  const open = (item: AdminQueueItem) => {
    setTarget(item);
    setChoice(undefined);
  };

  const close = () => {
    if (!saving) setTarget(null);
  };


  // Toasts carry no medicine, patient or physician names — only the fixed messages.
  const assign = async () => {
    if (!target || !choice) return;
    setSaving(true);
    const { data, error } = await assignReviewItemApi(target.id, choice);
    setSaving(false);
    if (data) {
      toastSuccess(MESSAGES.assigned);
      queue.mutate((prev = []) => prev.filter((i) => i.id !== target.id));
      setTarget(null);
      void refreshSummary();
      return;
    }
    switch (error?.code) {
      case "ITEM_NOT_ASSIGNABLE":
        toastError(MESSAGES.notAssignable);
        setTarget(null);
        void load();
        void refreshSummary();
        break;
      case "REVIEW_ITEM_NOT_FOUND":
        toastError(MESSAGES.itemGone);
        setTarget(null);
        void load();
        void refreshSummary();
        break;
      case "PHYSICIAN_NOT_ELIGIBLE":
        toastError(MESSAGES.notEligible);
        setChoice(undefined);
        break;
      case "PHYSICIAN_NOT_FOUND":
        toastError(MESSAGES.physicianGone);
        setChoice(undefined);
        void loadPhysicians();
        break;
      default:
        toastError(error?.message ?? "Unable to assign the item.");
    }
  };

  const columns: ColumnsType<AdminQueueItem> = [
    { title: "Medicine", dataIndex: "medicineName", key: "medicineName" },
    { title: "Issue", key: "issue", render: (_, i) => <span className="font-semibold text-[#0A2947]">{ISSUE_LABELS[i.triggerType]}</span> },
    { title: "Patient", dataIndex: "patientDisplayName", key: "patient" },
    {
      title: "Waiting since",
      key: "waitingSince",
      render: (_, i) => (
        <span className="inline-flex items-center gap-2">
          {formatRelativeTime(i.waitingSince)}
          {i.overdue && (
            <span className="rounded-full bg-[#FDECEF] px-2 py-0.5 text-[10px] font-bold text-[#E4002B]">Overdue</span>
          )}
        </span>
      ),
    },
    {
      title: "",
      key: "assign",
      align: "right",
      render: (_, i) => (
        <Button onClick={() => open(i)} className="!h-8 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-4 !text-xs !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white">
          Assign
        </Button>
      ),
    },
  ];

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
        <Table<AdminQueueItem>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={queue.loading}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: <EmptyState variant="compact" title={MESSAGES.empty} /> }}
          scroll={{ x: 820 }}
        />
      </div>

      <AppModal
        open={target !== null}
        onOpenChange={(o) => { if (!o) close(); }}
        title={<span className="text-[#0A2947]">Assign to a physician</span>}
        description={
          <span className="text-[#4B5563]">The physician you choose will be notified and can review it right away.</span>
        }
        width={480}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={saving} onClick={close} className={OUTLINE}>Cancel</Button>
            <Button disabled={!choice} loading={saving} onClick={() => void assign()} className={BRAND}>Assign item</Button>
          </div>
        }
      >
        <PhysicianPicker physicians={physicians} value={choice} onChange={setChoice} />
      </AppModal>
    </div>
  );
}
